import { useCallback, useEffect, useState } from "react";
import { Plus, Search, Loader2, X, Trash2, Mail, Phone, Building2 } from "lucide-react";
import { toast } from "sonner";
import { SenderAvatar } from "./MailApp";

export const ContactsView = ({ api, onCompose }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null); // {} for new, contact for edit
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/mail/contacts", { params: query ? { q: query } : {} });
      setContacts(data.contacts || []);
    } catch {
      toast.error("Could not load contacts");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q, load]);

  const save = async (form) => {
    setBusy(true);
    try {
      if (modal?.id) {
        await api.put(`/mail/contacts/${modal.id}`, form);
        toast.success("Contact updated");
      } else {
        await api.post("/mail/contacts", form);
        toast.success("Contact added");
      }
      setModal(null);
      load(q.trim());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the contact");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!modal?.id) return;
    setBusy(true);
    try {
      await api.delete(`/mail/contacts/${modal.id}`);
      toast.success("Contact deleted");
      setModal(null);
      load(q.trim());
    } catch {
      toast.error("Could not delete the contact");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="contacts-view">
      <div className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-white/10 flex-wrap">
        <h2 className="font-marcellus text-xl text-white">People</h2>
        <div className="flex items-center gap-2 flex-1 min-w-[180px] max-w-sm border border-white/15 bg-white/[0.02] px-3 py-2">
          <Search size={14} className="text-slate-500 shrink-0" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search contacts…"
            className="flex-1 min-w-0 bg-transparent text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none"
            data-testid="contacts-search"
          />
        </div>
        <button
          onClick={() => setModal({})}
          className="ml-auto flex items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-wide px-4 py-2.5 transition-colors"
          data-testid="contacts-new"
        >
          <Plus size={13} /> New contact
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-500 text-sm" data-testid="contacts-loading"><Loader2 size={16} className="animate-spin" /> Loading…</div>
        ) : contacts.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-slate-600" data-testid="contacts-empty">
            <Mail size={28} strokeWidth={1.2} />
            <p className="text-sm">{q ? "No matching contacts" : "No contacts yet — add your first one"}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="contacts-grid">
            {contacts.map((c) => (
              <div key={c.id} className="group flex items-center gap-3 border border-white/10 bg-white/[0.02] hover:border-nx-gold/40 px-4 py-3.5 transition-colors" data-testid={`contact-${c.id}`}>
                <SenderAvatar email={c.email} name={c.name} size={38} />
                <button onClick={() => setModal(c)} className="flex-1 min-w-0 text-left">
                  <p className="truncate text-sm text-white font-semibold">{c.name || c.email}</p>
                  <p className="truncate text-xs text-slate-500">{c.email}{c.company ? ` · ${c.company}` : ""}</p>
                </button>
                {c.email && (
                  <button
                    onClick={() => onCompose(c.email)}
                    className="p-2 text-slate-500 hover:text-nx-goldlight transition-colors"
                    title={`Email ${c.name || c.email}`}
                    aria-label={`Email ${c.name || c.email}`}
                    data-testid={`contact-email-${c.id}`}
                  >
                    <Mail size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {modal !== null && <ContactModal contact={modal} busy={busy} onSave={save} onDelete={remove} onClose={() => setModal(null)} />}
    </div>
  );
};

const ContactModal = ({ contact, busy, onSave, onDelete, onClose }) => {
  const isNew = !contact.id;
  const [name, setName] = useState(contact.name || "");
  const [email, setEmail] = useState(contact.email || "");
  const [phone, setPhone] = useState(contact.phone || "");
  const [company, setCompany] = useState(contact.company || "");
  const [notes, setNotes] = useState(contact.notes || "");

  const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";

  return (
    <div data-lenis-prevent className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-6" data-testid="contact-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-nx-navy2 border border-white/10 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-marcellus text-lg text-white">{isNew ? "New contact" : "Edit contact"}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors p-1" aria-label="Close" data-testid="contact-close"><X size={18} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave({ name, email, phone, company, notes }); }} className="flex flex-col gap-3 p-6">
          <input placeholder="Full name" className={input} value={name} onChange={(e) => setName(e.target.value)} data-testid="contact-name" />
          <div className="relative">
            <Mail size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input placeholder="Email address" className={`${input} pl-11`} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="contact-email" />
          </div>
          <div className="relative">
            <Phone size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input placeholder="Phone (optional)" className={`${input} pl-11`} value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="contact-phone" />
          </div>
          <div className="relative">
            <Building2 size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input placeholder="Company (optional)" className={`${input} pl-11`} value={company} onChange={(e) => setCompany(e.target.value)} data-testid="contact-company" />
          </div>
          <textarea placeholder="Notes (optional)" rows={2} className={`${input} resize-none`} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="contact-notes" />
          <div className="flex items-center justify-between gap-3 pt-1">
            {!isNew ? (
              <button type="button" onClick={onDelete} disabled={busy} className="flex items-center gap-2 text-xs tracking-wide text-red-400/90 hover:text-red-300 px-3 py-3 transition-colors" data-testid="contact-delete">
                <Trash2 size={13} /> Delete
              </button>
            ) : <span />}
            <button type="submit" disabled={busy} className="flex items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold text-sm tracking-wide px-8 py-3 transition-colors disabled:opacity-60" data-testid="contact-save">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
